import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';

// Utils
import { supabase } from 'utils/supabase-client';

import { clearUserData } from './userSlice';

// Adapter
const productsAdapter = createEntityAdapter({
  selectId: product => product.entityId,
  // Place Last Updated Products on top;
  sortComparer: (currentProduct, nextProduct) => {
    const currentProductDate = new Date(currentProduct.updatedAt);
    const nextProductDate = new Date(nextProduct.updatedAt);

    return nextProductDate.getTime() - currentProductDate.getTime();
  }
});

// Additional Initial State Options
const additionalInitialStateOptions = {
  isFetchingProducts: false,
  isUpdatingProducts: false
};

// Thunks
const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const {
        error,
        data
      } = await supabase.functions.invoke('upsert-product', {
        body: productData
      });

      if (error || data?.error) {
        throw error || data.error;
      }

      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.message });
    }
  }
);

const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async ({ productId }, { rejectWithValue }) => {
    try {
      const {
        error
      } = await supabase.from('products').delete()
        .eq('id', productId);

      if (error) {
        throw error;
      }

      return productId;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const { data: products, error } = await supabase
        .from('products')
        .select(`
          entityId:id,
          createdAt:created_at,
          updatedAt:updated_at,
          published,
          price,
          allergens,
          restaurantId:restaurant_id,
          isBeverage:is_beverage,
          imageUrl:image,
          translations: products_translations (
            locale,
            name,
            description,
            ingredients
          )
        `);

      if (error) {
        throw error;
      }

      const formattedProducts = products.map((product) => {
        const fallbackTranslations = {
          description: '',
          ingredients: '',
          title: ''
        };

        const translations = {};

        if (Array.isArray(product.translations) && product.translations.length) {
          for (const translation of product.translations) {
            const {
              description,
              ingredients,
              locale,
              name
            } = translation;

            if (description && !fallbackTranslations?.description) fallbackTranslations.description = description;
            if (ingredients && !fallbackTranslations?.ingredients) fallbackTranslations.ingredients = ingredients;
            if (name && !fallbackTranslations?.title) fallbackTranslations.title = name;

            if (!translations[locale]) translations[locale] = {};

            if (description) translations[locale].description = description;
            if (ingredients) translations[locale].ingredients = ingredients;
            if (name) translations[locale].title = name;
          }
        }

        return {
          ...product,
          fallbackTranslations,
          translations
        };
      });

      return formattedProducts;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ updatedProductData }, { rejectWithValue }) => {
    try {
      const {
        error,
        data
      } = await supabase.functions.invoke('upsert-product', {
        body: updatedProductData
      });

      if (error || data?.error) {
        throw error || data.error;
      }
      const { product } = data || {};

      // Get the fallback translations
      const fallbackTranslations = {
        description: '',
        ingredients: '',
        title: ''
      };

      const { translations } = product || {};
      const locales = Object.keys(translations || {});

      if (locales.length) {
        for (const locale of locales) {
          const {
            description,
            ingredients,
            title
          } = translations[locale] || {};

          if (description && !fallbackTranslations.description) fallbackTranslations.description = description;
          if (ingredients && !fallbackTranslations.ingredients) fallbackTranslations.ingredients = ingredients;
          if (title && !fallbackTranslations.title) fallbackTranslations.title = title;

          if (
            fallbackTranslations.description &&
            fallbackTranslations.ingredients &&
            fallbackTranslations.title
          ) {
            break;
          }
        }
      }

      const formattedProduct = {
        ...product,
        fallbackTranslations
      };

      return formattedProduct;
    } catch (error) {
      return rejectWithValue({ error: error?.message });
    }
  }
);

// Slice
const productsSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Fetch Products Handlers
      .addCase(fetchProducts.pending, (state) => {
        state.isFetchingProducts = true;
      })
      .addCase(fetchProducts.fulfilled, (state, { payload }) => {
        const products = payload;

        if (Array.isArray(products)) {
          productsAdapter.setAll(state, products);
        }

        state.isFetchingProducts = false;
      })
      .addCase(fetchProducts.rejected, (state) => {
        state.isFetchingProducts = false;
      })
      // Create Product Handlers
      .addCase(createProduct.pending, (state) => {
        state.isUpdatingProducts = true;
      })
      .addCase(createProduct.fulfilled, (state, { payload }) => {
        state.isUpdatingProducts = false;

        const { product } = payload || {};

        if (product) {
          productsAdapter.upsertOne(state, product);
        }
      })
      .addCase(createProduct.rejected, (state) => {
        state.isUpdatingProducts = false;
      })
      // Delete Product Handlers
      .addCase(deleteProduct.pending, (state) => {
        state.isUpdatingProducts = true;
      })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        state.isUpdatingProducts = false;

        const productId = payload;

        if (productId) {
          productsAdapter.removeOne(state, productId);
        }
      })
      .addCase(deleteProduct.rejected, (state) => {
        state.isUpdatingProducts = false;
      })
      // Handle Logout
      .addCase(clearUserData, () => ({
        ...productsAdapter.getInitialState()
      }))
      // Update Product Handlers
      .addCase(updateProduct.pending, (state) => {
        state.isUpdatingProducts = true;
      })
      .addCase(updateProduct.fulfilled, (state, { payload }) => {
        state.isUpdatingProducts = false;

        const product = payload;

        if (product) {
          productsAdapter.upsertOne(state, product);
        }
      })
      .addCase(updateProduct.rejected, (state) => {
        state.isUpdatingProducts = false;
      });
  },

  initialState: productsAdapter.getInitialState(additionalInitialStateOptions),

  name: 'products'
});

// Selectors
const productsSelectors = productsAdapter.getSelectors(state => state.products);

const {
  selectAll: selectProducts,
  selectById
} = productsSelectors;

const selectBeverages = createSelector(
  selectProducts,
  products => products.filter(product => product.isBeverage)
);

const selectDishes = createSelector(
  selectProducts,
  products => products.filter(product => !product.isBeverage)
);

const selectProductById = id => state => selectById(state, id);

const selectIsFetchingProducts = state => state.products.isFetchingProducts;

const selectIsUpdatingProducts = state => state.products.isUpdatingProducts;

export {
  createProduct,
  deleteProduct,
  fetchProducts,
  selectBeverages,
  selectDishes,
  selectIsFetchingProducts,
  selectIsUpdatingProducts,
  selectProductById,
  selectProducts,
  updateProduct
};

export default productsSlice.reducer;
